import React from "react";
import { Box, Tooltip } from "@mantine/core";
import { IconAlertCircleFilled } from "@tabler/icons-react";
import styles from "./DuplicateClientWarningIcon.module.css";

/** Default tooltip/accessible label pointing staff toward the merge flow. */
export const DUPLICATE_CLIENT_TOOLTIP =
  "This client is flagged as a potential duplicate. Visit the Clients page to review and merge the duplicate records.";

interface DuplicateClientWarningIconProps {
  /** Icon size in px. Defaults to 18. */
  size?: number;
  /** Override the tooltip + aria label. */
  label?: string;
}

/**
 * A red exclamation-in-a-circle flag shown next to a client's name when that
 * client is flagged as a potential duplicate. The tooltip is reachable via
 * hover, keyboard focus, and touch/tap so the message works on the phones staff
 * use. Shared by the calendar event popover and the Transaction Modal so the
 * two surfaces render the identical affordance.
 */
const DuplicateClientWarningIcon: React.FC<DuplicateClientWarningIconProps> = ({
  size = 18,
  label = DUPLICATE_CLIENT_TOOLTIP,
}) => (
  <Tooltip
    label={label}
    multiline
    w={240}
    withArrow
    // Enable focus + touch so the message is reachable via keyboard and on
    // phones (there's no hover on touch devices).
    events={{ hover: true, focus: true, touch: true }}
  >
    <Box
      component="span"
      role="img"
      aria-label={label}
      tabIndex={0}
      className={styles.icon}
    >
      <IconAlertCircleFilled size={size} color="var(--mantine-color-red-6)" />
    </Box>
  </Tooltip>
);

export default DuplicateClientWarningIcon;
